class CustomSelectSeries extends HTMLElement {
  constructor() {
    super();
    this.options = [];
    this.selectedValue = null;
    this._placeholder = "Select an option";
    this._pendingOptions = null;
    this._pendingValue = null;

    // Temporary value for mobile selection before confirmation
    this._tempValue = null;

    this._onHeaderClick = this._onHeaderClick.bind(this);
    this._onOptionsClick = this._onOptionsClick.bind(this);
    this._onConfirmClick = this._onConfirmClick.bind(this);
    this._onDocumentClick = this._onDocumentClick.bind(this);
    this._onOverlayClick = this._onOverlayClick.bind(this);
    this._onNativeChange = this._onNativeChange.bind(this);

    this._optionsEl = null;
    this._overlayEl = null;
    this._connectToken = 0;
  }

  _getOptionsEl() {
    return (
      this.querySelector(".custom-select-series-options-container")
      || this._optionsEl
      || null
    );
  }

  _getOverlayEl() {
    return (
      this.querySelector(".custom-select-series-overlay")
      || this._overlayEl
      || null
    );
  }

  _isNativeSelect() {
    return this.dataset.nativeSelect === "true";
  }

  _shouldTeleportOptions() {
    if (this.dataset.dropdownMode === "inline") return false;
    // Default mobile behavior is the bottom sheet picker used by older sections.
    return window.matchMedia("(max-width: 767px)").matches;
  }

  _syncInlineDropdownBounds() {
    const optionsEl = this._getOptionsEl();
    if (!optionsEl || this.dataset.dropdownMode !== "inline") return;

    const header = this.querySelector(".custom-select-series-header");
    const filterScroller = header?.closest(".wtb-filter-bar__scroll");
    if (!filterScroller?.classList.contains("is-dropdown-open")) {
      header?.scrollIntoView?.({
        block: "nearest",
        inline: "nearest",
        behavior: "auto",
      });
    }

    optionsEl.style.transform = "none";
    const rect = (header || this).getBoundingClientRect();
    const viewportHeight =
      window.visualViewport?.height || window.innerHeight || 600;
    const availableBelow = Math.floor(viewportHeight - rect.bottom - 12);
    const maxHeight = Math.max(96, Math.min(260, availableBelow));
    optionsEl.style.setProperty("--custom-select-series-max-height", `${maxHeight}px`);

    const viewportWidth =
      window.visualViewport?.width || window.innerWidth || 375;
    const margin = 12;
    const dropdownRect = optionsEl.getBoundingClientRect();
    let offsetX = 0;
    if (dropdownRect.right > viewportWidth - margin) {
      offsetX -= dropdownRect.right - (viewportWidth - margin);
    }
    if (dropdownRect.left + offsetX < margin) {
      offsetX += margin - (dropdownRect.left + offsetX);
    }

    optionsEl.style.transform = offsetX ? `translateX(${Math.round(offsetX)}px)` : "none";
  }

  _teleportOptionsBack() {
    if (!this._optionsEl) return;
    const wrapper = this.querySelector(".custom-select-series");
    if (wrapper) {
      if (this._overlayEl) wrapper.appendChild(this._overlayEl);
      wrapper.appendChild(this._optionsEl);
    }
    this._optionsEl = null;
    this._overlayEl = null;
  }

  static get observedAttributes() {
    return ["options", "class", "placeholder", "value"];
  }

  connectedCallback() {
    const connectToken = ++this._connectToken;
    this.render();
    requestAnimationFrame(() => {
      if (!this.isConnected || connectToken !== this._connectToken) return;
      this._addEventListeners();
      this._applyInitialAttributes();
    });
  }

  disconnectedCallback() {
    this._connectToken++;
    this.classList.remove("is-open");
    this._removeEventListeners();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;

    if (name === "placeholder") {
      this._placeholder = newValue || "Select an option";
      this._updatePlaceholder();
    } else if (name === "options") {
      try {
        const parsed = JSON.parse(newValue);
        this._pendingOptions = parsed;
        if (this.isConnected && this._isDOMReady()) {
          this.updateOptions(parsed);
        }
      } catch (e) {
        console.error("Invalid options format:", e);
      }
    } else if (name === "class") {
      this._updateClass(newValue);
    } else if (name === "value") {
      this._pendingValue = newValue;
      if (this.isConnected && this._isDOMReady()) {
        this.selectOption(newValue);
      }
    }
  }

  get value() {
    return this.selectedValue;
  }

  set value(val) {
    this.selectOption(val);
  }

  render() {
    if (this._isNativeSelect()) {
      this.innerHTML = `
        <div class="custom-select-series custom-select-series--native">
          <select class="custom-select-series-native-select" aria-label="${this._escapeHTML(this._placeholder)}"></select>
        </div>
      `;
      return;
    }

    this.innerHTML = `
      <div class="custom-select-series">
        <div class="custom-select-series-header flex items-center gap-2 bg-transparent transition-all duration-300 ease cursor-pointer">
          <span class="text-[14px] font-[400] tracking-[-0.28px] leading-[20px] text-black/[0.88]">${this._placeholder}</span>
          <div class="custom-select-series-arrow">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M6.50609 9.59438C6.77953 9.86781 7.22359 9.86781 7.49703 9.59438L10.997 6.09438C11.2705 5.82094 11.2705 5.37688 10.997 5.10344C10.7236 4.83 10.2795 4.83 10.0061 5.10344L7.00047 8.10906L3.99484 5.10563C3.72141 4.83219 3.27734 4.83219 3.00391 5.10563C2.73047 5.37906 2.73047 5.82313 3.00391 6.09656L6.50391 9.59656L6.50609 9.59438Z" fill="black" fill-opacity="0.88"/>
            </svg>
          </div>
        </div>
        
        <div class="custom-select-series-overlay"></div>
        
        <div class="custom-select-series-options-container">
          <div class="custom-select-series-options felx flex-col gap-1 max-h-[50vh] overflow-y-auto"></div>
          <div class="custom-select-series-footer">
            <button type="button" class="custom-select-series-confirm-btn">Select</button>
          </div>
        </div>
      </div>
    `;
  }

  updateOptions(options) {
    if (!Array.isArray(options)) {
      console.error("Options must be an array.");
      return;
    }

    this.options = options;

    if (this._isNativeSelect()) {
      this._renderNativeOptions();
      return;
    }

    const container = this._getOptionsEl();
    if (!container) return;

    const list = container.querySelector(".custom-select-series-options");
    if (!list) return;

    list.innerHTML = this.options
      .map(
        (option) => `
        <div class="custom-select-series-option${option.value === this.selectedValue ? " selected" : ""} text-black/[0.88] [&.selected]:text-[#FF5A00] py-[14px] px-4" 
             data-value="${option.value}"
             ${option.code ? `data-code="${option.code}"` : ""}
             ${option.next ? `data-next="${option.next}"` : ""}>
          <span class="option-label text-[16px] font-[600] leading-[125%] tracking-[-0.16px]">${option.label}</span>
          ${this._renderCheckIcon()}
        </div>
      `,
      )
      .join("");

    if (container.classList.contains("active")) {
      requestAnimationFrame(() => this._syncInlineDropdownBounds());
    }
  }

  _renderNativeOptions() {
    const select = this.querySelector(".custom-select-series-native-select");
    if (!select) return;

    select.innerHTML = "";

    const placeholderOption = new Option(this._placeholder, "__placeholder__");
    placeholderOption.hidden = true;
    placeholderOption.disabled = true;
    select.appendChild(placeholderOption);

    this.options.forEach((option) => {
      const optionEl = new Option(option.label, option.value);
      if (option.code) optionEl.dataset.code = option.code;
      if (option.next) optionEl.dataset.next = option.next;
      select.appendChild(optionEl);
    });

    this._syncNativeSelectValue();
  }

  _syncNativeSelectValue() {
    if (!this._isNativeSelect()) return;
    const select = this.querySelector(".custom-select-series-native-select");
    if (!select) return;

    const showPlaceholder =
      this.selectedValue === null ||
      this.selectedValue === undefined ||
      (this.selectedValue === "" && this.dataset.filterVariant === "pill");
    select.value = showPlaceholder ? "__placeholder__" : this.selectedValue;
    select.classList.toggle("has-value", !showPlaceholder);
    this._syncNativeSelectWidth(showPlaceholder);
  }

  _syncNativeSelectWidth(showPlaceholder = false) {
    const select = this.querySelector(".custom-select-series-native-select");
    if (!select) return;

    const selectedOption = this.options.find((option) => option.value === this.selectedValue);
    const label = showPlaceholder
      ? this._placeholder
      : selectedOption?.label || select.selectedOptions?.[0]?.textContent || this._placeholder;

    requestAnimationFrame(() => {
      if (!this.isConnected) return;

      const styles = window.getComputedStyle(select);
      const canvas = CustomSelectSeries._measurementCanvas
        || (CustomSelectSeries._measurementCanvas = document.createElement("canvas"));
      const context = canvas.getContext("2d");
      if (!context) return;

      context.font = styles.font || `${styles.fontWeight} ${styles.fontSize} ${styles.fontFamily}`;

      const textWidth = context.measureText(label).width;
      const padding =
        (parseFloat(styles.paddingLeft) || 0) +
        (parseFloat(styles.paddingRight) || 0);
      const border =
        (parseFloat(styles.borderLeftWidth) || 0) +
        (parseFloat(styles.borderRightWidth) || 0);
      const arrowGutter = 24;

      select.style.width = `${Math.ceil(textWidth + padding + border + arrowGutter)}px`;
    });
  }

  _renderCheckIcon() {
    return `
      <svg class="check-icon hidden" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M16.6666 5L7.49992 14.1667L3.33325 10" stroke="#FF5A00" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;
  }

  selectOption(value, isTemp = false, isUserInteraction = false) {
    const selected = this.options.find((opt) => opt.value === value);
    if (!selected) return;

    if (!isTemp) {
      this.selectedValue = selected.value;
      if (this._isNativeSelect()) {
        this._syncNativeSelectValue();
      }
      const span = this.querySelector(".custom-select-series-header span");
      const showPlaceholder =
        selected.value === "" && this.dataset.filterVariant === "pill";
      if (span) {
        span.textContent = showPlaceholder ? this._placeholder : selected.label;
      }
      this.querySelector(".custom-select-series-header")?.classList.toggle(
        "has-value",
        !showPlaceholder,
      );
    }

    const optionsContainer = this._getOptionsEl();
    optionsContainer
      ?.querySelectorAll(".custom-select-series-option")
      .forEach((el) => {
        const isSelected = el.getAttribute("data-value") === value;
        el.classList.toggle("selected", isSelected);
      });

    if (!isTemp) {
      // If not temp (Desktop or confirm), dispatch event
      this.dispatchEvent(
        new CustomEvent("custom-select-series-change", {
          detail: {
            value: selected.value,
            label: selected.label,
            next: selected.next || false,
            code: selected.code || "",
            isUserInteraction,
          },
          bubbles: true,
          composed: true,
        }),
      );
    }
  }

  reset() {
    this.selectedValue = null;
    this._tempValue = null;
    this._updatePlaceholder();
    this.close();
  }

  close() {
    const optionsEl = this._getOptionsEl();
    const overlayEl = this._getOverlayEl();

    optionsEl?.classList.remove("active");
    optionsEl?.style.removeProperty("transform");
    overlayEl?.classList.remove("active");
    this.classList.remove("is-open");
    this.querySelector(".custom-select-series-header")?.classList.remove(
      "active",
    );

    this._teleportOptionsBack();
  }

  open() {
    const optionsEl = this._getOptionsEl();
    const overlayEl = this._getOverlayEl();
    if (!optionsEl) return;

    if (this._shouldTeleportOptions()) {
      const section = this.closest(".where-to-buy-v4")
        || this.closest(".where-to-buy-v3")
        || document.body;
      // Teleport both overlay and options
      if (overlayEl) section.appendChild(overlayEl);
      section.appendChild(optionsEl);

      this._optionsEl = optionsEl;
      this._overlayEl = overlayEl;
    }

    // Sync temp value with current value when opening
    this._tempValue = this.selectedValue;
    if (this._tempValue !== null && this._tempValue !== undefined) {
      this.selectOption(this._tempValue, true);
    }

    optionsEl.classList.add("active");
    this._syncInlineDropdownBounds();
    requestAnimationFrame(() => this._syncInlineDropdownBounds());
    if (overlayEl) overlayEl.classList.add("active");
    this.classList.add("is-open");
    this.querySelector(".custom-select-series-header")?.classList.add("active");
  }

  _addEventListeners() {
    if (this._isNativeSelect()) {
      this.querySelector(".custom-select-series-native-select")?.addEventListener(
        "change",
        this._onNativeChange,
      );
      return;
    }

    this.querySelector(".custom-select-series-header")?.addEventListener(
      "click",
      this._onHeaderClick,
    );

    // We need to attach listeners to the elements even if they are teleported
    // But since we teleport on open, we can rely on the fact that we query them dynamically or keep references
    // However, it's safer to use event delegation or attach to the elements themselves

    // Since _getOptionsEl returns the element wherever it is
    const optionsEl = this.querySelector(
      ".custom-select-series-options-container",
    );
    if (optionsEl) {
      optionsEl.addEventListener("click", this._onOptionsClick);
      const confirmBtn = optionsEl.querySelector(
        ".custom-select-series-confirm-btn",
      );
      if (confirmBtn)
        confirmBtn.addEventListener("click", this._onConfirmClick);
    }

    const overlayEl = this.querySelector(".custom-select-series-overlay");
    if (overlayEl) overlayEl.addEventListener("click", this._onOverlayClick);

    document.addEventListener("click", this._onDocumentClick);
  }

  _removeEventListeners() {
    if (this._isNativeSelect()) {
      this.querySelector(".custom-select-series-native-select")?.removeEventListener(
        "change",
        this._onNativeChange,
      );
      return;
    }

    this.querySelector(".custom-select-series-header")?.removeEventListener(
      "click",
      this._onHeaderClick,
    );

    // Remove from stored refs if teleported
    if (this._optionsEl) {
      this._optionsEl.removeEventListener("click", this._onOptionsClick);
      const confirmBtn = this._optionsEl.querySelector(
        ".custom-select-series-confirm-btn",
      );
      if (confirmBtn)
        confirmBtn.removeEventListener("click", this._onConfirmClick);
    } else {
      const optionsEl = this.querySelector(
        ".custom-select-series-options-container",
      );
      if (optionsEl) {
        optionsEl.removeEventListener("click", this._onOptionsClick);
        const confirmBtn = optionsEl.querySelector(
          ".custom-select-series-confirm-btn",
        );
        if (confirmBtn)
          confirmBtn.removeEventListener("click", this._onConfirmClick);
      }
    }

    if (this._overlayEl) {
      this._overlayEl.removeEventListener("click", this._onOverlayClick);
    } else {
      const overlayEl = this.querySelector(".custom-select-series-overlay");
      if (overlayEl)
        overlayEl.removeEventListener("click", this._onOverlayClick);
    }

    document.removeEventListener("click", this._onDocumentClick);
  }

  _onHeaderClick() {
    const optionsEl = this._getOptionsEl();
    if (!optionsEl) return;
    const isOpening = !optionsEl.classList.contains("active");

    if (isOpening) {
      this.open();
    } else {
      this.close();
    }
  }

  _onNativeChange(event) {
    const value = event.target.value;
    if (value === "__placeholder__") return;
    this.selectOption(value, false, event.isTrusted);
  }

  _onOptionsClick(event) {
    const option = event.target.closest(".custom-select-series-option");
    if (option) {
      const val = option.getAttribute("data-value");

      if (this._shouldTeleportOptions()) {
        this._tempValue = val;
        this.selectOption(val, true);
      } else {
        this.selectOption(val, false, event.isTrusted);
        this.close();
      }
    }
  }

  _onConfirmClick(event) {
    // Mobile only
    if (this._tempValue !== null && this._tempValue !== undefined) {
      this.selectOption(this._tempValue, false, event.isTrusted);
    }
    this.close();
  }

  _onOverlayClick(event) {
    this.close();
  }

  _onDocumentClick(event) {
    const optionsEl = this._getOptionsEl();
    // If teleported, checking contains might be tricky if event target is inside teleported element
    // But since we listen on document, event.target should be the actual element.

    const header = this.querySelector(".custom-select-series-header");

    const clickedInsideHeader = header && header.contains(event.target);
    const clickedInsideOptions = optionsEl && optionsEl.contains(event.target);
    const clickedInsideOverlay =
      this._getOverlayEl() && this._getOverlayEl().contains(event.target);

    if (
      !clickedInsideHeader
      && !clickedInsideOptions
      && !clickedInsideOverlay
    ) {
      // If it's active, close it
      if (optionsEl && optionsEl.classList.contains("active")) {
        this.close();
      }
    }
  }

  _updateClass(newClass) {
    const wrapper = this.querySelector(".custom-select-series");
    if (!wrapper || !newClass) return;

    String(newClass)
      .split(/\s+/)
      .filter(Boolean)
      .forEach((className) => wrapper.classList.add(className));
  }

  _updatePlaceholder() {
    if (this._isNativeSelect()) {
      const placeholderOption = this.querySelector(
        '.custom-select-series-native-select option[value="__placeholder__"]',
      );
      if (placeholderOption) placeholderOption.textContent = this._placeholder;
      this._syncNativeSelectValue();
      return;
    }

    if (this.selectedValue === null || this.selectedValue === undefined) {
      const span = this.querySelector(".custom-select-series-header span");
      if (span) {
        span.textContent = this._placeholder;
      }
      this.querySelector(".custom-select-series-header")?.classList.remove(
        "has-value",
      );
    }
  }

  _applyInitialAttributes() {
    const optionsAttr = this.getAttribute("options");
    let optionsToApply = this._pendingOptions;

    if (!optionsToApply && optionsAttr) {
      try {
        optionsToApply = JSON.parse(optionsAttr);
      } catch (e) {
        console.error("Invalid options format:", e);
      }
    }

    if (!optionsToApply && this.options.length) {
      optionsToApply = this.options;
    }

    if (optionsToApply) {
      this.updateOptions(optionsToApply);
    }
    this._pendingOptions = null;

    const hasPendingValue = this._pendingValue !== null && this._pendingValue !== undefined;
    const hasValueAttr = this.hasAttribute("value");
    const valueToApply = hasPendingValue ? this._pendingValue : this.getAttribute("value");

    if ((hasPendingValue || hasValueAttr) && valueToApply) {
      this.selectOption(valueToApply);
    }
    this._pendingValue = null;

    this._updatePlaceholder();
  }

  _isDOMReady() {
    if (this._isNativeSelect()) {
      return !!this.querySelector(".custom-select-series-native-select");
    }
    return !!this._getOptionsEl();
  }

  _escapeHTML(value) {
    return String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }
}

customElements.define("custom-select-series", CustomSelectSeries);
